/**
 * Handles the highlighting of a vector feature on the map.
 */

import cloneDeep from 'lodash/cloneDeep'
import OLRenderFeature from 'ol/render/Feature'

import * as PoziUtils from 'api/utils'
import { SILENT_UPDATE } from 'data/geojsonio'
import { WHATS_HERE_LAYER_TITLEID } from 'drawer/tabs/info/WhatsHereUtils'

const highlightVectorFeature = (olFeature, olLayer) => {
  console.debug('HighlightVectorFeature', olFeature, olLayer)

  if (!olFeature) {
    console.log('HighlightVectorFeature: feature cannot provided. Doing nothing.', olFeature)
    return
  }

  // Ths following situation can happen with Vector Tile Features, where the
  // feature is a 'RenderFeature' which has very limited functionality:
  // it cannot be cloned for example, nor can its geometry be extracted.
  // TODO (maybe): use ol's recommended way of vector tile selection:
  //      https://openlayers.org/en/latest/examples/vector-tile-selection.html
  if (!olFeature.clone) {
    console.log('HighlightVectorFeature: feature cannot be cloned for highlighting. Doing nothing.', olFeature)
    return
  }

  // Wrapper style function for the highlight feature that keeps all existing
  // styling of the original feature but will remove any fill values to make
  // it transparent and therefore not interfere with the original feature
  const highlightFeatureStyle = (feature, resolution) => {
    // console.debug('highlightVectorFeature - highlightFeatureStyle - old style', feature, oldStyle)

    // If the old style is a function, then call that to return the actual
    // styles, otherwise just add it to the styles array. We also make a deep copy in order to not
    // interfere with the style of the original layer
    let newStyles = []
    if (typeof oldStyle === 'function') {
      const oldStylesArray = cloneDeep(oldStyle(feature, resolution))
      // console.debug('highlightVectorFeature - highlightFeatureStyle - old styles array', oldStylesArray)

      if (oldStylesArray) {
        newStyles = newStyles.concat(oldStylesArray)
      }
    } else {
      newStyles = newStyles.concat(oldStyle)
    }

    // Loop through every style and remove the fill
    newStyles.forEach(style => {
      if (style.setFill) {
        style.setFill(null)
      }
    })

    return newStyles
  }

  // Make a copy of the feature
  const highlightFeature = olFeature.clone()

  // If the style of the feature was set through the old way through feature properties,
  // force the fill to be transparent
  if (highlightFeature.get('style-fill-color')) {
    highlightFeature.set('style-fill-color', 'rgba(0,0,0,0)', SILENT_UPDATE)
  }

  const isRenderFeature = highlightFeature instanceof OLRenderFeature

  console.debug('highlightVectorFeature -highlightfeature is render feature', highlightFeature)

  // Apply the new style but only if it or its layer already had a style and the feature
  // is not a render feature
  const oldStyle = (!isRenderFeature && highlightFeature.getStyle()) || olLayer?.getStyle()
  if (oldStyle && !isRenderFeature) {
    highlightFeature.setStyle(highlightFeatureStyle)
  } else {
    // console.debug('highlightVectorFeature - no old style', highlightFeature)
  }

  // Add the highlight feature to the What's Here layer
  const source = PoziUtils.getOpenLayersObjectFromTitleID(WHATS_HERE_LAYER_TITLEID).getSource()
  if (source) {
    source.addFeature(highlightFeature)
  } else {
    console.warn('Could not highlight feature due to layer source not being available.')
  }
}

export default highlightVectorFeature
